import { For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CardView, SimplifiedView } from '../../lib/machine-card';
import { CardAction } from './CardAction';
import { LiveReason } from './LiveReason';
import { MoveNote } from './MoveNote';
import { MoveWorkButton } from './MoveWorkButton';
import type { NowSend, NowWork } from './NowZone';
import { SendWorkButton } from './SendWorkButton';
import { StartingRows } from './StartingRows';
import { StateMark } from './StateMark';

// The simplified card (#6785, prototype compactHTML, `:2389-2424`): the same four rows
// for every card in every state, so a grid of them reads as one shape. The status line (a
// tinted line with its action when loud, the reason and a quiet action otherwise; a blocker
// folds into it), the roles running with the job count, one combined slice bar (an empty
// track reading "-" until epics are reported, #6745), and three mini bars with last seen.
// Below them, for an admin on an own-org machine, the "Send work" button (#6770, the
// prototype's `.cmp-acts`, `:2402-2403`), the jobs sent here that read Starting, and the
// form itself (`children`), as the prototype puts it after `.cmp-acts` (`:2404-2405`).
// Beside it, "Move work" (#6772), which opens the full card with its Move work panel.

export function SimplifiedCard(props: {
  readonly view: CardView;
  readonly onAction: () => void;
  readonly send?: NowSend | undefined;
  /** "Move work" (#6772): present only where the panel can open; it opens the full card. */
  readonly work?: NowWork | undefined;
  /** The Send work note and form, mounted below the buttons. */
  readonly children?: JSX.Element;
}): JSX.Element {
  const s = (): SimplifiedView => props.view.simplified;
  return (
    <div class="mission-simple">
      <div
        class="mission-simple__status"
        data-tone={s().status.tone}
        data-loud={s().status.loud ? '' : undefined}
        role={s().status.loud ? 'status' : undefined}
      >
        <Show when={s().status.loud}>
          <StateMark state={props.view.state} />
        </Show>
        <LiveReason
          class="mission-simple__reason"
          reason={s().status.reason}
          liveReason={s().status.liveReason}
        />
        <Show when={s().status.action}>
          {(label) => (
            <CardAction
              label={label()}
              tone={s().status.tone}
              size={s().status.loud ? 'banner' : 'line'}
              onPress={props.onAction}
            />
          )}
        </Show>
      </div>
      <p class="mission-simple__row">
        <span class="mission-simple__what">{s().what}</span>
        <span class="mission-simple__count">{s().count}</span>
      </p>
      <div class="mission-simple__row">
        <Show
          when={s().slices}
          fallback={
            <>
              <span class="mission-simple__track" />
              <span class="mission-simple__count">{t('fleet.machines.not_reported')}</span>
            </>
          }
        >
          {(sl) => (
            <>
              <span
                class="mission-simple__track"
                role="img"
                aria-label={t('fleet.machines.card_slices_running_aria', {
                  done: sl().done,
                  total: sl().total,
                  running: sl().running,
                })}
              >
                <i
                  class="mission-simple__seg--done"
                  style={{ width: `${(sl().done / sl().total) * 100}%` }}
                />
                <i
                  class="mission-simple__seg--live"
                  style={{ width: `${(sl().running / sl().total) * 100}%` }}
                />
              </span>
              <span class="mission-simple__count">{sl().text}</span>
            </>
          )}
        </Show>
      </div>
      <p class="mission-simple__row mission-simple__res">
        <Show
          when={s().res}
          fallback={
            <span class="mission-simple__quiet">{t('fleet.machines.card_res_unknown')}</span>
          }
        >
          {(res) => (
            <For each={res()}>
              {(bar) => (
                <span class="mission-simple__mini" data-tone={bar.tone}>
                  <span>{bar.label}</span>
                  <span class="mission-simple__mini-track">
                    <i style={{ width: `${bar.fill}%` }} />
                  </span>
                  <span class="mission-simple__mini-value">{bar.value}</span>
                </span>
              )}
            </For>
          )}
        </Show>
        <span class="mission-card__seen">{props.view.footer.seen}</span>
      </p>
      {/* Each action on its OWN gate (#6772 review round 4): Send work on its controller, Move
          work on the move controller's rules (`MissionCard`'s `moveWork`); one never hides the
          other. */}
      <Show when={props.send !== undefined || props.work !== undefined}>
        <div class="mission-simple__acts">
          <Show when={props.send}>
            {(send) => (
              <SendWorkButton
                variant="simple"
                offline={send().offline}
                expanded={send().expanded}
                onOpen={send().onOpen}
              />
            )}
          </Show>
          <Show when={props.work}>
            {(work) => (
              <MoveWorkButton variant="simple" expanded={work().expanded} onOpen={work().onOpen} />
            )}
          </Show>
        </div>
      </Show>
      <Show when={(props.send?.starting.length ?? 0) > 0}>
        <StartingRows rows={props.send?.starting ?? []} />
      </Show>
      {/* A move's note (#6772): a simplified card has no Now or Mission zone, and it is still a
          rendered card, so a job or epic note for it reads here. */}
      <MoveNote zone="now" />
      <MoveNote zone="mission" />
      {props.children}
    </div>
  );
}
